import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CircleCheck, CircleHelp, CircleX, RefreshCw } from "lucide-react";
import PageShell, { Section } from "@/components/site/PageShell";
import { Note, Prose } from "@/components/site/ui";
import { COMPONENTS, HISTORY_SINCE, INCIDENTS, LABELS, MAINTENANCE, SUBSCRIBE } from "@/content/status";
import { API } from "@/lib/auth";

const ICONS = { operational: CircleCheck, unmonitored: CircleHelp, checking: CircleHelp };

// Sonde réelle : l'API et la base sont interrogées à chaque chargement.
async function probe() {
  const out = { api: "major", db: "major", frontend: "operational" };
  try {
    const r = await fetch(`${API}/health`, { signal: AbortSignal.timeout(8000) });
    const body = await r.json().catch(() => ({}));
    out.api = r.ok ? "operational" : "degraded";
    out.db = body.db === "ok" ? "operational" : r.ok ? "degraded" : "major";
  } catch { /* l'état par défaut (panne) est déjà le bon */ }
  return out;
}

export default function Status() {
  const [states, setStates] = useState(null);
  const [checkedAt, setCheckedAt] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async () => {
    setBusy(true);
    setStates(await probe());
    setCheckedAt(new Date());
    setBusy(false);
  }, []);

  useEffect(() => { run(); }, [run]);

  const stateOf = c => {
    if (!c.probe) return "unmonitored";
    if (!states) return "checking";
    return states[c.probe] ?? "checking";
  };

  const monitored = COMPONENTS.filter(c => c.probe).map(stateOf);
  const allGood = monitored.length > 0 && monitored.every(s => s === "operational");
  const anyDown = monitored.some(s => s === "major" || s === "partial");

  const banner = !states ? { cls: "is-wait", text: "Vérification de l'état des services…" }
    : allGood ? { cls: "is-ok", text: "Tous les services supervisés sont opérationnels" }
    : anyDown ? { cls: "is-down", text: "Incident en cours sur un ou plusieurs services" }
    : { cls: "is-warn", text: "Performances dégradées" };

  return (
    <PageShell eyebrow="État des services" title="Perform fonctionne-t-il ?"
      lead="Cette page interroge réellement nos services à chaque chargement. Elle n'affiche pas un état enregistré à l'avance.">
      <Section>
        <div className={`status-banner ${banner.cls}`} data-testid="status-banner">
          <span className="status-dot" />
          <strong>{banner.text}</strong>
          <button type="button" onClick={run} disabled={busy} data-testid="status-refresh" aria-label="Revérifier">
            <RefreshCw size={13} className={busy ? "animate-spin" : undefined} />Revérifier
          </button>
        </div>
        {checkedAt && <p className="status-checked">Dernière vérification à {checkedAt.toLocaleTimeString("fr-FR")}.</p>}
      </Section>

      <Section title="Composants">
        <div className="status-list">
          {COMPONENTS.map(c => {
            const st = stateOf(c);
            const Icon = ICONS[st] || CircleX;
            return (
              <div key={c.key} className={`status-row is-${st}`} data-testid={`status-${c.key}`}>
                <div>
                  <strong>{c.name}</strong>
                  <small>{c.desc}</small>
                  {c.note && <em>{c.note}</em>}
                </div>
                <span className="status-tag"><Icon size={13} />{LABELS[st]}</span>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Incidents en cours">
        {INCIDENTS.length === 0
          ? <Prose><p>Aucun incident en cours.</p></Prose>
          : INCIDENTS.map(i => <div key={i.id}>{i.title}</div>)}
      </Section>

      <Section title="Maintenances programmées">
        {MAINTENANCE.length === 0
          ? <Prose><p>Aucune maintenance programmée.</p></Prose>
          : MAINTENANCE.map(m => <div key={m.id}>{m.title}</div>)}
      </Section>

      <Section title="Historique">
        <Prose>
          <p>
            Aucun incident n'a été enregistré depuis l'ouverture du service, le {HISTORY_SINCE}.
            L'historique démarre à cette date : nous n'affichons pas d'antériorité que nous n'avons pas mesurée.
          </p>
        </Prose>
      </Section>

      <Section title="Être prévenu" lead={SUBSCRIBE.intro}>
        <div className="card-grid">
          {SUBSCRIBE.channels.map(c => (
            <div key={c.key} className="info-card is-muted">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <h3>{c.label}</h3>
                <span className="pill" style={{ fontSize: 10 }}>Bientôt</span>
              </div>
              <p style={{ marginTop: 8 }}>{c.desc}</p>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 18 }}><Note>{SUBSCRIBE.note}</Note></div>
      </Section>

      <Section>
        <Prose><p>Un problème que cette page ne signale pas ? Écrivez à <Link to="/contact">notre support</Link>.</p></Prose>
      </Section>
    </PageShell>
  );
}
